/**
 * TEMPORARY local-testing API mocks, for endpoints whose backend is not deployed yet.
 *
 * Off unless `REACT_APP_MOCK_API` lists the domain (comma-separated, e.g.
 * `REACT_APP_MOCK_API=automaticCharge`), and never in a production build. Put it only in a
 * local, gitignored env file (`.env.onboard.dev`).
 *
 * A domain mock replaces the service object exported by `services/apiDigitalTicket/<domain>.ts`
 * with one that has the same methods, answering axios-shaped responses and errors, so the
 * sagas and pages run unchanged. To remove a domain's mock: delete its file in this folder and
 * the `mock:` switch at the bottom of the domain's service file. Delete this folder when no
 * domain uses it.
 */

const MOCKED_DOMAINS =
  process.env.NODE_ENV === 'production'
    ? []
    : (process.env.REACT_APP_MOCK_API || '')
        .split(',')
        .map((domain) => domain.trim())
        .filter(Boolean);

export const isMockEnabled = (domain: string) =>
  MOCKED_DOMAINS.includes(domain);

const MOCK_LATENCY_MS = 400;

const wait = () =>
  new Promise((resolve) => {
    setTimeout(resolve, MOCK_LATENCY_MS);
  });

/**
 * An axios-like success response, after a short simulated latency.
 * @param {any} data the response body
 * @param {object} headers response headers (e.g. `totalcount` for paginated lists)
 * @return {Promise<object>} the response
 */
export const mockResponse = async (data: any, headers: object = {}) => {
  await wait();
  return { status: 200, data, headers };
};

/**
 * An axios-like error, with the `{ message, internalCode }` body the backend sends.
 * @param {number} status the HTTP status
 * @param {string} internalCode the backend internal code
 * @param {string} message the backend message
 * @return {Promise<never>} the rejected error
 */
export const mockError = async (
  status: number,
  internalCode: string,
  message: string,
) => {
  await wait();
  const error: any = new Error(`Mock API ${status}: ${internalCode}`);
  error.response = { status, data: { message, internalCode }, headers: {} };
  throw error;
};

/**
 * State kept in localStorage under `mockApi:<domain>`, so a reload keeps it (the pages resume
 * from the backend status). Cleared with `localStorage.removeItem('mockApi:<domain>')`.
 * @param {string} domain the mocked domain
 * @param {T} initial the state when nothing is stored
 * @return {object} get/set/reset of the domain state
 */
export const createMockStore = <T>(domain: string, initial: T) => {
  const key = `mockApi:${domain}`;
  const get = (): T => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : initial;
    } catch (e) {
      return initial;
    }
  };
  const set = (state: T) => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (e) {
      // Storage unavailable: the mock keeps working, without persistence.
    }
  };
  const reset = () => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Nothing to clear.
    }
  };
  return { get, set, reset };
};
